import { useEffect, useRef, useState } from "react";
import BackButton from "../../../../components/Button/BackButton";
import Button from "../../../../components/Button/Button";
import FormInput from "../../../../components/Form/FormInput";
import FormSelect from "../../../../components/Form/FormSelect";
import { getCountries } from "../../../../utils/constants/countries";
import { LogoPreview } from "./ProfileSettingsCard";
import { btn } from "../../../../components/Button/buttonStyles";

const countries = getCountries();

const EMPTY = {
  legal_name: "",
  tax_number: "",
  vat_number: "",
  website: "",
  country: "",
  state_region: "",
  city: "",
  postal_code: "",
  address: "",
};

const toForm = (profile) =>
  Object.keys(EMPTY).reduce(
    (acc, key) => ({ ...acc, [key]: profile?.[key] ?? "" }),
    {}
  );

const ProfileSettingsForm = ({ profile, onSave, onCancel }) => {
  const [form, setForm] = useState(toForm(profile));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  // The chosen logo is held here and only sent when Update is pressed, so
  // nothing changes on the server until the form is saved.
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);

  // The preview is an object URL. This cleanup runs both when it is replaced
  // and when the form unmounts, so each one is released exactly once.
  useEffect(
    () => () => {
      if (logoPreview) URL.revokeObjectURL(logoPreview);
    },
    [logoPreview]
  );

  const clearStagedLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.legal_name.trim()) {
      setErrors({ legal_name: "Name is required." });
      return;
    }

    setSaving(true);
    const ok = await onSave(form, { logoFile, removeLogo });
    setSaving(false);
    if (ok) onCancel();
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setRemoveLogo(false);
  };

  const handleRemoveLogo = () => {
    clearStagedLogo();
    // Only flag a deletion when there is a stored logo to delete; clearing a
    // freshly picked file just cancels that choice.
    setRemoveLogo(Boolean(profile?.logo_url));
  };

  const shownLogo = logoPreview ?? (removeLogo ? null : profile?.logo_url);

  return (
    <div>
      <div className="mb-5 flex items-center gap-3">
        <BackButton onClick={onCancel} />
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">
          Edit Profile Settings
        </h1>
      </div>

      <form
        onSubmit={handleSubmit}
        noValidate
        className="space-y-5 rounded-lg border border-gray-100 bg-white p-5 shadow-sm"
      >
        <div className="grid grid-cols-1 items-start gap-x-5 gap-y-4 md:grid-cols-2">
          <FormInput
            label="Name"
            name="legal_name"
            value={form.legal_name}
            onChange={handleChange}
            error={errors.legal_name}
            placeholder="Company or brand name used on documents"
            required
          />
          <FormInput
            label="Website"
            name="website"
            value={form.website}
            onChange={handleChange}
            error={errors.website}
            placeholder="example.com"
          />
          <FormInput
            label="PAN / Tax Number"
            name="tax_number"
            value={form.tax_number}
            onChange={handleChange}
            error={errors.tax_number}
          />
          <FormInput
            label="GSTIN / VAT Number"
            name="vat_number"
            value={form.vat_number}
            onChange={handleChange}
            error={errors.vat_number}
          />
          <FormSelect
            label="Country"
            name="country"
            value={form.country}
            onChange={handleChange}
            options={countries}
            error={errors.country}
          />
          <FormInput
            label="State / Province / Region"
            name="state_region"
            value={form.state_region}
            onChange={handleChange}
            error={errors.state_region}
          />
          <FormInput
            label="City"
            name="city"
            value={form.city}
            onChange={handleChange}
            error={errors.city}
          />
          <FormInput
            label="Postal / ZIP Code"
            name="postal_code"
            value={form.postal_code}
            onChange={handleChange}
            error={errors.postal_code}
          />
          <div className="md:col-span-2">
            <FormInput
              label="Address"
              name="address"
              value={form.address}
              onChange={handleChange}
              error={errors.address}
            />
          </div>
        </div>

        <div className="border-t border-gray-100 pt-4">
          <p className="mb-1.5 block text-xs font-medium text-gray-600">Logo</p>
          <div className="flex flex-wrap items-center gap-3">
            <LogoPreview url={shownLogo} />
            <input
              ref={fileRef}
              type="file"
              accept="image/png, image/jpeg, image/jpg, image/webp"
              className="hidden"
              onChange={handleFile}
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => fileRef.current?.click()}
            >
              {shownLogo ? "Replace logo" : "Upload logo"}
            </Button>
            {shownLogo && (
              <Button
                type="button"
                variant="dangerSoft"
                size="sm"
                onClick={handleRemoveLogo}
              >
                Remove
              </Button>
            )}
            <span className="text-[11px] text-gray-500">
              PNG, JPG or WEBP, up to 5MB.
            </span>
          </div>
        </div>

        <div className="space-y-3 border-t border-gray-100 pt-3">
          <span className="block text-xs text-gray-500">
            Fields marked with <span className="text-danger-500">*</span> are mandatory.
          </span>
          <div className="flex gap-2 text-sm">
            <button
              type="submit"
              disabled={saving}
              className={btn("primary", "sm")}
            >
              {saving ? "Updating..." : "Update"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className={btn("secondary", "sm")}
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProfileSettingsForm;
